import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { roundTo } from "../utils/general_util";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const username = localStorage.getItem("username");
    const email = localStorage.getItem("email");

    const [rankingsTopBcr, setRankingsTopBcr] = useState(null);
    const [rankingsTopNab, setRankingsTopNab] = useState(null);
    const [monthlyTrends, setMonthlyTrends] = useState(null);

    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState("");

    const navigate = useNavigate();

    // Darker chart palette
    const CHART = useMemo(
        () => ({
            barFill: "rgba(15, 23, 42, 0.85)", // slate-900
            barHover: "rgba(15, 23, 42, 0.95)",
            barBorder: "rgba(15, 23, 42, 1)",
            pointFill: "rgba(15, 23, 42, 1)",
            pointBorder: "rgba(255, 255, 255, 1)",
            grid: "rgba(148, 163, 184, 0.25)",
            tick: "rgba(30, 41, 59, 0.9)",
        }),
        []
    );

    const userRegistration = async () => {
        try {
            const response = await axiosInstance.post("/user/register", {
                email,
                username,
            });
            localStorage.setItem("role", response.data.role);
            window.dispatchEvent(new Event("authUpdated"));
        } catch (error) {
            console.error("There was a problem with the registration request:", error);
        }
    };

    const fetchStatistics = async () => {
        setStatsLoading(true);
        setStatsError("");
        try {
            const [bcrRes, nabRes, trendRes] = await Promise.all([
                axiosInstance.get("/statistics/rankings/top", {
                    params: { mode: "bcr", limit: 10, min_tickets: 1 },
                }),
                axiosInstance.get("/statistics/rankings/top", {
                    params: { mode: "nab", limit: 10, min_tickets: 1 },
                }),
                axiosInstance.get("/statistics/trends/monthly", {
                    params: { months: 12 },
                }),
            ]);

            setRankingsTopBcr(bcrRes.data);
            setRankingsTopNab(nabRes.data);
            setMonthlyTrends(trendRes.data);
        } catch (e) {
            console.error(e);
            setStatsError("Unable to load statistics.");
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        if (username && email) userRegistration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username, email]);

    useEffect(() => {
        fetchStatistics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateTicket = () => navigate("/create-ticket");

    const formatCurrency = (currency, value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return "—";
        const code = (currency && currency !== "N/A" ? currency : "AUD")
            .toString()
            .trim()
            .toUpperCase();
        try {
            return new Intl.NumberFormat("en-AU", {
                style: "currency",
                currency: code,
                currencyDisplay: "code",
                maximumFractionDigits: 2,
            }).format(value);
        } catch {
            return `${Number(value).toFixed(2)} ${code}`.trim();
        }
    };

    const formatBcr = (value) => {
        if (value === null || value === undefined || Number.isNaN(value)) return "—";
        return String(roundTo(Number(value), 1));
    };

    // IMPORTANT: keep charts mounted (don’t swap them out for Loading/Error blocks)
    const hasAnyData = Boolean(rankingsTopBcr || rankingsTopNab || monthlyTrends);

    const StatusLine = () => {
        if (statsError) return <p className="mt-4 text-sm text-red-600">{statsError}</p>;
        if (statsLoading && hasAnyData) return <p className="mt-4 text-sm text-slate-600">Updating charts…</p>;
        if (statsLoading && !hasAnyData) return <p className="mt-4 text-sm text-slate-600">Loading…</p>;
        return null;
    };

    const baseScaleStyles = useMemo(
        () => ({
            x: {
                ticks: { maxRotation: 0, autoSkip: true, color: CHART.tick },
                grid: { color: CHART.grid },
            },
            y: {
                ticks: { color: CHART.tick },
                grid: { color: CHART.grid },
            },
        }),
        [CHART.grid, CHART.tick]
    );

    const chartOptionsBase = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            interaction: { mode: "index", intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
            },
            scales: {
                x: { ...baseScaleStyles.x, grid: { display: false } },
                y: { ...baseScaleStyles.y, beginAtZero: true },
            },
        }),
        [baseScaleStyles.x, baseScaleStyles.y]
    );

    const lineOptionsBase = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            interaction: { mode: "index", intersect: false },
            plugins: {
                legend: {
                    position: "bottom",
                    labels: {
                        usePointStyle: true,
                        boxWidth: 10,
                        boxHeight: 10,
                        color: CHART.tick,
                    },
                },
                tooltip: { enabled: true },
            },
            scales: {
                x: { ...baseScaleStyles.x, grid: { display: false } },
                y: { ...baseScaleStyles.y, beginAtZero: false },
            },
            elements: {
                line: { tension: 0.25, borderWidth: 2 },
                point: { radius: 4, hoverRadius: 6, borderWidth: 2 },
            },
        }),
        [CHART.tick, baseScaleStyles.x, baseScaleStyles.y]
    );

    const renderTopBarCharts = (data, mode) => {
        const groups = data?.groups || [];
        const isBcr = mode === "bcr";

        if (groups.length === 0) {
            return <p className="mt-4 text-sm text-slate-600">No data.</p>;
        }

        return groups.map((g) => {
            const barData = {
                labels: g.labels,
                datasets: [
                    {
                        label: isBcr ? "Avg BCR" : "Total NAB",
                        data: g.values,
                        backgroundColor: CHART.barFill,
                        hoverBackgroundColor: CHART.barHover,
                        borderColor: CHART.barBorder,
                        borderRadius: 10,
                        borderSkipped: false,
                        maxBarThickness: 44,
                    },
                ],
            };

            const options = {
                ...chartOptionsBase,
                plugins: {
                    ...chartOptionsBase.plugins,
                    title: {
                        display: true,
                        text: `Currency: ${g.currency}`,
                        font: { weight: "bold" },
                        padding: { top: 4, bottom: 16 },
                    },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                const val = ctx.parsed.y;
                                if (isBcr) return `Avg BCR: ${formatBcr(val)}`;
                                return `Total NAB: ${formatCurrency(g.currency, val)}`;
                            },
                            afterLabel: (ctx) => {
                                const idx = ctx.dataIndex;
                                const t = g.ticketCounts?.[idx] ?? 0;
                                return `Tickets: ${t}`;
                            },
                        },
                    },
                },
            };

            return (
                <div key={`${mode}-${g.currency}`} className="w-full mt-4">
                    <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                        <div className="h-56 sm:h-72 md:h-80 lg:h-[420px]">
                            <Bar data={barData} options={options} />
                        </div>
                    </div>
                </div>
            );
        });
    };

    const renderMonthlyTrendCharts = () => {
        const labels = monthlyTrends?.labels || [];
        const seriesByCurrency = monthlyTrends?.seriesByCurrency || {};
        const currencies = Object.keys(seriesByCurrency);

        if (!labels.length || currencies.length === 0) {
            return <p className="mt-4 text-sm text-slate-600">No trend data.</p>;
        }

        const datasetsWithAlpha = (metric) =>
            currencies.map((c, i) => {
                const alpha = Math.max(0.35, 0.95 - i * 0.18);
                return {
                    label: c,
                    data: seriesByCurrency[c]?.[metric] || [],
                    spanGaps: true,
                    borderColor: `rgba(15, 23, 42, ${alpha})`,
                    backgroundColor: `rgba(15, 23, 42, ${Math.min(0.14, alpha * 0.14)})`,
                    pointBackgroundColor: CHART.pointFill,
                    pointBorderColor: CHART.pointBorder,
                };
            });

        const bcrLineData = { labels, datasets: datasetsWithAlpha("avgBcr") };
        const nabLineData = { labels, datasets: datasetsWithAlpha("totalNab") };

        const bcrOptions = {
            ...lineOptionsBase,
            plugins: {
                ...lineOptionsBase.plugins,
                title: {
                    display: true,
                    text: "Average Benefit Cost Ratio trend (month to month)",
                    font: { weight: "bold" },
                    padding: { top: 4, bottom: 16 },
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: ${formatBcr(ctx.parsed.y)}`,
                    },
                },
            },
        };

        const nabOptions = {
            ...lineOptionsBase,
            plugins: {
                ...lineOptionsBase.plugins,
                title: {
                    display: true,
                    text: "Total Net Annual Benefit trend (month to month)",
                    font: { weight: "bold" },
                    padding: { top: 4, bottom: 16 },
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) =>
                            `${ctx.dataset.label}: ${formatCurrency(ctx.dataset.label, ctx.parsed.y)}`,
                    },
                },
            },
        };

        return (
            <div className="mt-4 grid gap-4 min-w-0">
                <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                    <div className="h-56 sm:h-72 md:h-80 lg:h-[420px]">
                        <Line data={bcrLineData} options={bcrOptions} />
                    </div>
                </div>

                <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                    <div className="h-56 sm:h-72 md:h-80 lg:h-[420px]">
                        <Line data={nabLineData} options={nabOptions} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200 min-w-0">
                <header className="mb-6 flex items-center justify-between gap-4 min-w-0">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
                    </div>
                </header>

                <p className="rounded-xl bg-slate-100 p-4 ring-1 ring-slate-200">Welcome to Kaizen App.</p>
                <p className="mt-4 rounded-xl bg-slate-100 p-4 ring-1 ring-slate-200">
                    If you have any questions, please contact{" "}
                    <a href="mailto:technology@ciilock.com" className="break-all text-blue-600 hover:underline">
                        technology@ciilock.com
                    </a>
                </p>

                <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={handleCreateTicket}
                        className="rounded-full bg-blue-600 px-5 py-2.5 text-md text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:bg-blue-600 focus:ring-offset-1 transition"
                    >
                        Create new ticket
                    </button>
                    <button
                        type="button"
                        onClick={fetchStatistics}
                        className="rounded-full bg-slate-600 px-5 py-2.5 text-md text-white shadow-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:bg-slate-600 focus:ring-offset-1 transition"
                    >
                        Refresh statistics
                    </button>
                </div>

                <StatusLine />

                {/* Statistics */}
                <div className="mt-6 grid gap-6 lg:grid-cols-2 min-w-0">
                    <section className="min-w-0 rounded-2xl bg-white p-6 ring-1 ring-slate-200 shadow-sm overflow-hidden">
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-slate-900">Benefit Cost Ratio</h2>
                            <p className="mt-1 text-sm text-slate-600">Top performers (bar chart per currency)</p>
                        </div>

                        {rankingsTopBcr ? renderTopBarCharts(rankingsTopBcr, "bcr") : null}
                    </section>

                    <section className="min-w-0 rounded-2xl bg-white p-6 ring-1 ring-slate-200 shadow-sm overflow-hidden">
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-slate-900">Net Annual Benefit</h2>
                            <p className="mt-1 text-sm text-slate-600">Top performers (bar chart per currency)</p>
                        </div>

                        {rankingsTopNab ? renderTopBarCharts(rankingsTopNab, "nab") : null}
                    </section>
                </div>

                <section className="mt-6 min-w-0 rounded-2xl bg-white p-6 ring-1 ring-slate-200 shadow-sm overflow-hidden">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-slate-900">Month-to-month trends</h2>
                        <p className="mt-1 text-sm text-slate-600">
                            Aggregated by month and currency (last 12 months)
                        </p>
                    </div>

                    {monthlyTrends ? renderMonthlyTrendCharts() : null}
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
